import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { signInWithEmailAndPassword, signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import { ArrowLeft, ArrowRight2, InfoCircle, ProfileCircle } from "iconsax-reactjs";
import OtpInput from "./OtpInput";
import LoginIllustration from "./LoginIllustration";
import { leadStatusLabel } from "./DealerLeads";
import { markPhoneSignIn, takePhoneSignIn } from "../utils/phoneSignIn";
import { MOBILE_LENGTH, editMobile, formatMobile } from "../utils/mobileInput";
import "./Login.css";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;
const SUPPORT_EMAIL = "support@gharsetu.com";

// The main path a lead takes in the Leads panel, shown on the brand panel.
const LEAD_PATH = ["New", "Contacted", "Visit Scheduled", "Converted"];

// The GharSetu team signs in with email and password at /team. Dealers only
// ever see the phone sign-in, and nothing on it points here.
const TEAM_PATH = "/team";

export function isTeamSignInPath() {
  return window.location.pathname.replace(/\/+$/, "") === TEAM_PATH;
}

const formatPhone = (digits) => `+91 ${formatMobile(digits)}`;

function describeError(err) {
  switch (err?.code) {
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/user-disabled":
      return "This account has been turned off. Contact GharSetu support.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "That email and password don't match.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes, then try again.";
    case "auth/network-request-failed":
    case "functions/unavailable":
      return "Can't reach GharSetu. Check your internet connection and try again.";
    default:
      // sendOtp and verifyOtp return messages written for people ("OTP has
      // expired..."); "INTERNAL" is Firebase's text for an unexpected failure.
      return err?.message && err.message !== "INTERNAL"
        ? err.message
        : "Something went wrong. Please try again.";
  }
}

export default function Login({ notice = null, onNoticeDismiss }) {
  const teamMode = isTeamSignInPath();
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [mobile, setMobile] = useState({ digits: "", caret: 0 }); // the number field
  const phoneRef = useRef(null);
  const [phone, setPhone] = useState(""); // the 10 digits the OTP went to
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(null); // "send" | "verify" | "resend" | "email"
  const [resendAt, setResendAt] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  const noticeHeadingRef = useRef(null);

  // App answers a verified number that has no dealer account with a notice
  // (see App.js); the sign-in that was in flight ends here.
  useEffect(() => {
    if (!notice) return;
    setBusy(null);
    setCode("");
    if (notice.kind === "check-failed") {
      // The OTP was used up by the attempt, so the next step is a new one.
      setStep("code");
      setResendAt(0);
      setError("We couldn't open your account just now. Request a new OTP and try again.");
      onNoticeDismiss();
      return;
    }
    noticeHeadingRef.current?.focus();
  }, [notice, onNoticeDismiss]);

  // Each edit of the number is re-spaced as 98765 43210, so put the caret
  // back where the dealer was typing. A new object every edit, even an ignored
  // 11th digit, so this runs after React resets the field's text.
  useLayoutEffect(() => {
    const input = phoneRef.current;
    if (input && document.activeElement === input) {
      input.setSelectionRange(mobile.caret, mobile.caret);
    }
  }, [mobile]);

  useEffect(() => {
    const tick = () => {
      const left = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
      setResendIn(left);
      return left;
    };
    if (tick() === 0) return undefined;
    const timer = setInterval(() => {
      if (tick() === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendAt]);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (busy) return;
    const { digits } = mobile;
    if (digits.length !== MOBILE_LENGTH) {
      setError("Enter your 10-digit mobile number.");
      return;
    }
    if (!/^[6-9]/.test(digits)) {
      setError("That isn't a valid Indian mobile number. Check it and try again.");
      return;
    }
    setError("");
    setStatus("");
    setBusy("send");
    try {
      // There is deliberately no "is this a dealer?" check before the OTP:
      // answering that without authentication would let anyone walk the
      // number space and list every dealer. The account is checked once the
      // code is verified (App.js).
      await httpsCallable(functions, "sendOtp")({ phoneNumber: digits });
      setPhone(digits);
      setCode("");
      setStep("code");
      setResendAt(Date.now() + RESEND_SECONDS * 1000);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  const verifyOtp = async (value) => {
    if (busy) return;
    if (value.length !== CODE_LENGTH) {
      setError("Enter all 6 digits of the OTP.");
      return;
    }
    setError("");
    setStatus("");
    setBusy("verify");
    try {
      const { data } = await httpsCallable(functions, "verifyOtp")({ phoneNumber: phone, otpCode: value });
      markPhoneSignIn(phone);
      await signInWithCustomToken(auth, data.customToken);
      // App checks the account from here: a dealer lands in the portal, any
      // other number comes back with a notice. "Verifying" stays up until then.
    } catch (err) {
      takePhoneSignIn();
      setError(describeError(err));
      setCode("");
      setBusy(null);
    }
  };

  const resendOtp = async () => {
    if (busy || resendIn > 0) return;
    setError("");
    setStatus("");
    setBusy("resend");
    try {
      await httpsCallable(functions, "sendOtp")({ phoneNumber: phone });
      setCode("");
      setResendAt(Date.now() + RESEND_SECONDS * 1000);
      setStatus(`A new OTP is on its way to ${formatPhone(phone)}.`);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  const changeNumber = () => {
    setStep("phone");
    setCode("");
    setError("");
    setStatus("");
  };

  const startOver = () => {
    onNoticeDismiss();
    setMobile({ digits: "", caret: 0 });
    changeNumber();
  };

  const signInWithEmail = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setError("");
    setBusy("email");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      setError(describeError(err));
      setBusy(null);
    }
  };

  const errorLine = error ? (
    <p className="login-error" id="login-error" role="alert">
      <InfoCircle size={16} variant="Linear" aria-hidden="true" />
      <span>{error}</span>
    </p>
  ) : null;

  let pane;
  if (teamMode) {
    pane = (
      <>
        <header className="login-head">
          <h1 className="login-title">GharSetu team sign in</h1>
          <p className="login-lede">Sign in with your GharSetu team email and password.</p>
        </header>
        <form className="login-form" onSubmit={signInWithEmail} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "login-error" : undefined}
              autoFocus
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="login-password">Password</label>
            <input
              id="login-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "login-error" : undefined}
            />
          </div>
          {errorLine}
          <button className="btn-primary btn-lg login-submit" type="submit" disabled={busy === "email"}>
            {busy === "email" ? <><span className="btn-spinner" aria-hidden="true" />Signing in…</> : "Sign in"}
          </button>
        </form>
        <p className="login-note"><a href="/">Dealer sign in</a></p>
      </>
    );
  } else if (notice) {
    const notDealer = notice.kind === "not-dealer";
    pane = (
      <>
        <span className="login-notice-icon" aria-hidden="true">
          <ProfileCircle size={24} variant="Linear" />
        </span>
        <header className="login-head">
          <h1 className="login-title" tabIndex={-1} ref={noticeHeadingRef}>
            {notDealer ? "This number isn't a dealer account" : "No dealer account on this number yet"}
          </h1>
          {notDealer ? (
            <p className="login-lede">
              <strong>{formatPhone(notice.phone)}</strong> has a GharSetu account, but not a dealer one.
              To use it as a dealer, contact GharSetu support: message Customer Support in the
              GharSetu app, or email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          ) : (
            <p className="login-lede">
              <strong>{formatPhone(notice.phone)}</strong> isn't registered with GharSetu.
              Dealer accounts are set up in the GharSetu app:
            </p>
          )}
        </header>
        {!notDealer && (
          <ol className="login-steps">
            <li><span>Install the GharSetu app and sign in with this number.</span></li>
            <li>
              <span>
                During profile setup, switch on <strong>Register as Real Estate Consultant</strong> and
                pin your office on the map.
              </span>
            </li>
            <li><span>Come back here and sign in with the same number.</span></li>
          </ol>
        )}
        <button type="button" className="btn-secondary btn-lg login-submit" onClick={startOver}>
          Use a different number
        </button>
      </>
    );
  } else if (step === "code") {
    pane = (
      <>
        <button type="button" className="login-back" onClick={changeNumber} disabled={busy === "verify"}>
          <ArrowLeft size={16} variant="Linear" aria-hidden="true" />
          Change number
        </button>
        <header className="login-head">
          <h1 className="login-title">Enter the OTP</h1>
          <p className="login-lede" id="login-code-hint">
            We sent a 6-digit OTP by SMS to <strong>{formatPhone(phone)}</strong>.
          </p>
        </header>
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); verifyOtp(code); }} noValidate>
          <div className="field">
            <label className="sr-only" htmlFor="login-otp">6-digit OTP</label>
            <OtpInput
              id="login-otp"
              value={code}
              length={CODE_LENGTH}
              onChange={(value) => { setCode(value); setError(""); }}
              onComplete={verifyOtp}
              readOnly={busy === "verify"}
              invalid={!!error}
              autoFocus
              describedBy={error ? "login-code-hint login-error" : "login-code-hint"}
            />
          </div>
          {errorLine}
          <button className="btn-primary btn-lg login-submit" type="submit" disabled={busy === "verify"}>
            {busy === "verify" ? <><span className="btn-spinner" aria-hidden="true" />Verifying…</> : "Verify and sign in"}
          </button>
        </form>
        <div className="login-resend">
          {resendIn > 0 ? (
            <p>
              Didn't get it? Resend in{" "}
              <span className="login-countdown">0:{String(resendIn).padStart(2, "0")}</span>
            </p>
          ) : (
            <p>
              Didn't get it?{" "}
              <button type="button" className="btn-link" onClick={resendOtp} disabled={!!busy}>
                {busy === "resend" ? "Sending…" : "Resend OTP"}
              </button>
            </p>
          )}
          <p className="login-status" role="status">{status}</p>
        </div>
      </>
    );
  } else {
    pane = (
      <>
        <header className="login-head">
          <h1 className="login-title">Sign in to your dealer account</h1>
          <p className="login-lede">
            Enter the mobile number you use on GharSetu. We'll send you a 6-digit OTP by SMS.
          </p>
        </header>
        <form className="login-form" onSubmit={sendOtp} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="login-phone">
              Mobile number<span className="sr-only"> (India, +91)</span>
            </label>
            <div className={`login-phone${error ? " is-invalid" : ""}`}>
              <span className="login-phone-code" aria-hidden="true">+91</span>
              <input
                id="login-phone"
                ref={phoneRef}
                className="login-phone-input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="98765 43210"
                value={formatMobile(mobile.digits)}
                onChange={(e) => {
                  const { value, selectionStart } = e.target;
                  setMobile(editMobile(mobile.digits, {
                    value,
                    caret: selectionStart ?? value.length,
                    inputType: e.nativeEvent.inputType,
                  }));
                  setError("");
                }}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "login-error" : undefined}
                autoFocus
              />
            </div>
          </div>
          {errorLine}
          <button className="btn-primary btn-lg login-submit" type="submit" disabled={busy === "send"}>
            {busy === "send" ? <><span className="btn-spinner" aria-hidden="true" />Sending OTP…</> : "Get OTP"}
          </button>
        </form>
        <p className="login-note">
          New to GharSetu? Register as a dealer in the GharSetu app, then sign in here with the
          same number.
        </p>
      </>
    );
  }

  return (
    <div className="login-screen">
      <aside className="login-brand">
        <div className="login-lockup">
          <img src="/logo.png" alt="" className="login-lockup-logo" />
          <div className="login-lockup-text">
            <span className="login-lockup-name">GharSetu</span>
            <span className="login-lockup-role">{teamMode ? "Team" : "Dealer portal"}</span>
          </div>
        </div>

        <div className="login-art">
          <LoginIllustration />
        </div>

        <div className="login-pitch">
          <p className="login-pitch-title">
            {teamMode ? "Run the GharSetu marketplace." : "Your listings and leads, in one place."}
          </p>
          <p className="login-pitch-text">
            {teamMode
              ? "Support chats, moderation, banners, notifications and settings for the GharSetu team."
              : "See which properties are drawing enquiries, answer every new lead, and keep your listings and offices up to date."}
          </p>
          {!teamMode && (
            <ol className="login-path" aria-label="Every lead, from new to converted">
              {LEAD_PATH.map((stage, index) => (
                <li key={stage} className="login-path-step">
                  {index > 0 && (
                    <ArrowRight2 size={14} variant="Linear" className="login-path-arrow" aria-hidden="true" />
                  )}
                  <span className="login-path-chip">{leadStatusLabel(stage)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </aside>

      <main className="login-main">
        <div className="login-pane">
          {pane}
          <p className="login-help">
            Need help? <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        </div>
      </main>
    </div>
  );
}
